/**
 * @author NikolaV
 */
import { TreeNode } from '@shared/ui-native/Tree';
import { OutsideClick } from '@shared/ui-react/react-outside-click-handler';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Popper } from 'react-popper';
import './Dropdown.scss';

export interface IContextMenuItem {
    label?: string;
    icon?: string;
    action?: () => void;
    enabled?: (node: TreeNode) => boolean;
    checked?: boolean | null;
    customElement?: JSX.Element;
}

class VirtualPopperReference {
    rect = null;

    constructor(mouseX: number, mouseY: number) {
        this.rect = {
            left: mouseX,
            right: mouseX + 1,
            top: mouseY,
            bottom: mouseY + 1,
            height: 1,
            width: 1,
        };
    }

    getBoundingClientRect() {
        return this.rect;
    }

    get clientWidth() {
        return this.getBoundingClientRect().width;
    }

    get clientHeight() {
        return this.getBoundingClientRect().height;
    }
}

export class ContextMenu extends React.Component<{
    render: any;
    disabled?: boolean;
    children?: React.ReactNode;
}> {
    state = {
        show: false,
        mouseX: null,
        mouseY: null,
    };

    popperProps = {
        placement: 'bottom-start' as any,
        positionFixed: true,
        modifiers: {
            preventOverflow: {
                boundariesElement: 'viewport' as any,
                escapeWithReference: false,
            },
        },
    };

    render() {
        const ref = new VirtualPopperReference(this.state.mouseX, this.state.mouseY);

        return (
            <>
                <div onContextMenu={this.handleMouseClick} className='context-menu-wrapper'>
                    {this.props.children}
                </div>

                {!this.state.show || this.props.render == null
                    ? null
                    : ReactDOM.createPortal(
                          <Popper {...this.popperProps} referenceElement={ref}>
                              {({ placement, ref, style }) => (
                                  <div ref={ref} style={{ zIndex: 99999, ...style }} data-placement={placement}>
                                      <OutsideClick onOutsideClick={this.close}>
                                          <div onClick={this.close} className='dropdown-react-wrapper'>
                                              {this.props.render()}
                                          </div>
                                      </OutsideClick>
                                  </div>
                              )}
                          </Popper>,
                          document.body
                      )}
            </>
        );
    }

    handleMouseClick = e => {
        e.preventDefault();
        if (this.props.disabled) {
            return;
        }
        this.setState({
            show: true,
            mouseX: e.pageX,
            mouseY: e.pageY,
        });
    };

    close = () => {
        this.setState({
            show: false,
        });
    };
}

export const renderContextMenuAtPosition = (items: IContextMenuItem[], pageX: number, pageY: number, domId: string, onClose: Function, closeOnBodyClick: boolean = true) => {
    const el = document.createElement('div');
    el.setAttribute('ik-context-menu-holder', 'true');
    el.id = domId;
    document.body.appendChild(el);

    ReactUtils.safeRender(
        <ContextMenuModel
            items={items}
            pageX={pageX}
            pageY={pageY}
            domId={domId}
            closeOnBodyClick={closeOnBodyClick}
            onClose={() => {
                if (el) {
                    ReactUtils.safeUnmountComponentAtNode(el);
                    el.remove();
                }
                onClose();
            }}
        />,
        el
    );
};

class ContextMenuModel extends React.Component<{
    items: IContextMenuItem[];
    pageX: number;
    pageY: number;
    domId: string;
    closeOnBodyClick: boolean;
    onClose: () => void;
}> {
    popperProps = {
        placement: 'bottom-start' as any,
        positionFixed: true,
        modifiers: {
            preventOverflow: {
                boundariesElement: 'viewport' as any,
                escapeWithReference: false,
            },
        },
    };

    render() {
        const ref = new VirtualPopperReference(this.props.pageX, this.props.pageY);
        const hasAnyIcon = this.props.items.some(item => !!item?.icon || !!item?.checked !== null);

        return (
            <Popper {...this.popperProps} referenceElement={ref}>
                {({ placement, ref, style }) => (
                    <div ref={ref} style={{ zIndex: 999999, ...style }} data-placement={placement}>
                        <OutsideClick onOutsideClick={this.props.onClose}>
                            <div onClick={this.onBodyClick} className='dropdown-react-wrapper'>
                                <ul className='dropdown-list clickable'>
                                    {this.props.items.map((item, index) => {
                                        if (item === null) return <li key={index} style={{ padding: 0, borderWidth: 2 }}></li>;

                                        return (
                                            <li key={index} onClick={() => item.action && item.action()} className={!!item.customElement ? 'unstyled' : ''}>
                                                {item.customElement || (
                                                    <>
                                                        {(item.icon || hasAnyIcon) && (
                                                            <span
                                                                style={{ width: 18, height: 18, marginRight: 3, justifyContent: 'center', filter: 'saturate(0)' }}
                                                                className='flex-row ai-center'
                                                            >
                                                                {this.getIcon(item)}
                                                            </span>
                                                        )}
                                                        <span>{item.label}</span>
                                                    </>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </OutsideClick>
                    </div>
                )}
            </Popper>
        );
    }

    getIcon = item => {
        if (item?.checked != null) {
            return <input type='checkbox' checked={item.checked} />;
        }
        if (typeof item?.icon === 'string') {
            return <i className={item.icon} />;
        }
        if (typeof item?.icon === 'object') {
            return item?.icon;
        }
        return null;
    };

    private readonly onBodyClick = e => {
        if (this.props.closeOnBodyClick) this.props.onClose();
    };
}
